'use client';
import React, { useState } from 'react';
import { Users, Fuel, CheckCircle, XCircle, ChevronRight, X } from 'lucide-react';
import BookingModal from './BookingModal';

export interface Vehicle {
  id: string;
  name: string;
  type: string;
  image: string;
  imageAlt: string;
  seats: number;
  ac: boolean;
  pricePerKm: number;
  available: boolean;
  badge: string;
  features: string[];
  description: string;
}

// BACKEND INTEGRATION: Replace with API call to /api/vehicles to get real-time availability
export const fleetData: Vehicle[] = [
{
  id: 'vehicle-swift-dzire',
  name: 'Swift Dzire',
  type: 'Sedan',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc20140d-1768380108457.png",
  imageAlt: 'White Swift Dzire sedan parked on clean road with city background',
  seats: 4,
  ac: true,
  pricePerKm: 14,
  available: true,
  badge: 'Most Popular',
  features: ['AC', 'Music System', 'GPS Tracking', 'Verified Driver'],
  description: 'Perfect for city rides and short outstation trips.'
},
{
  id: 'vehicle-innova-crysta',
  name: 'Innova Crysta',
  type: 'SUV',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_177893f29-1768377418212.png",
  imageAlt: 'Silver Toyota Innova Crysta SUV on highway with mountains in background',
  seats: 7,
  ac: true,
  pricePerKm: 18,
  available: true,
  badge: 'Premium',
  features: ['AC', 'Spacious', 'Luggage Space', 'Long Drive Ready'],
  description: 'Ideal for family trips and outstation journeys.'
},
{
  id: 'vehicle-ertiga',
  name: 'Ertiga',
  type: 'MPV',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bce212d4-1768362508626.png",
  imageAlt: 'Gray Maruti Ertiga MPV parked in front of hotel entrance',
  seats: 6,
  ac: true,
  pricePerKm: 16,
  available: false,
  badge: 'Family Choice',
  features: ['AC', '6 Seater', 'Comfortable', 'GPS'],
  description: 'Great for groups and family outings.'
},
{
  id: 'vehicle-hyundai-aura',
  name: 'Hyundai Aura',
  type: 'Sedan',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1bc20140d-1768380108457.png",
  imageAlt: 'White Hyundai Aura sedan on clean road in Delhi',
  seats: 4,
  ac: true,
  pricePerKm: 13,
  available: true,
  badge: 'Budget Friendly',
  features: ['AC', 'Fuel Efficient', 'GPS', 'Clean Interior'],
  description: 'Economical and comfortable for daily commutes.'
},
{
  id: 'vehicle-tempo-traveller',
  name: 'Tempo Traveller',
  type: 'Van',
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_11fa0872b-1768440186823.png",
  imageAlt: 'White Tempo Traveller van with large windows on mountain road',
  seats: 14,
  ac: true,
  pricePerKm: 22,
  available: true,
  badge: 'Group Travel',
  features: ['AC', '14 Seater', 'Luggage Rack', 'Tour Ready'],
  description: 'Perfect for group tours and pilgrimages.'
}];


export default function FleetSection() {
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null);
  const [showBooking, setShowBooking] = useState(false);

  const handleSelectCar = (vehicle: Vehicle) => {
    if (!vehicle.available) return;
    setSelectedVehicle(vehicle);
    setShowBooking(true);
    setTimeout(() => {
      document.getElementById('booking-form-area')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  return (
    <section id="fleet" className="py-20 px-6 lg:px-10 section-bg">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-xs font-bold text-primary uppercase tracking-widest mb-3 block">
            Our Fleet
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4">
            Choose Your <span className="gradient-blue-text">Premium Ride</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Select a vehicle below to unlock the booking form. All cabs come with verified drivers.
          </p>
        </div>

        {/* Fleet Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5 mb-12">
          {fleetData.map((vehicle) =>
          <div
            key={vehicle.id}
            className={`glass-card rounded-2xl overflow-hidden card-hover border transition-all duration-300 ${
            selectedVehicle?.id === vehicle.id ?
            'border-primary glow-blue-sm' : 'border-border/20'} ${
            !vehicle.available ? 'opacity-70' : ''}`}>
            
              {/* Image */}
              <div className="relative h-44 overflow-hidden">
                <img
                src={vehicle.image}
                alt={vehicle.imageAlt}
                className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />

                {/* Badge */}
                <div className="absolute top-3 left-3">
                  <span className="text-xs font-bold bg-primary text-white px-2 py-1 rounded-full">
                    {vehicle.badge}
                  </span>
                </div>

                {/* Availability */}
                <div className="absolute top-3 right-3">
                  {vehicle.available ?
                <span className="badge-available text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                      Available
                    </span> :

                <span className="badge-booked text-xs font-bold px-2 py-1 rounded-full flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                      Booked
                    </span>
                }
                </div>

                {/* Price on enquiry */}
                <div className="absolute bottom-3 right-3">
                  <span className="text-amber-300 font-semibold text-xs bg-black/70 backdrop-blur-sm px-2 py-1 rounded-lg">
                    Enquire for fare
                  </span>
                </div>
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-bold text-white text-base">{vehicle.name}</h3>
                    <p className="text-xs text-muted-foreground">{vehicle.type}</p>
                  </div>
                </div>

                {/* Specs */}
                <div className="flex items-center gap-3 mb-3">
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Users size={12} className="text-primary" />
                    {vehicle.seats} Seats
                  </span>
                  <span className="flex items-center gap-1 text-xs text-muted-foreground">
                    <Fuel size={12} className="text-primary" />
                    {vehicle.ac ? 'AC' : 'Non-AC'}
                  </span>
                </div>

                <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{vehicle.description}</p>

                {/* Price note */}
                <p className="text-xs text-amber-300/80 mb-3">
                  💬 Contact driver for latest fare.
                </p>

                {/* Features */}
                <div className="flex flex-wrap gap-1 mb-4">
                  {vehicle.features.slice(0, 2).map((f) =>
                <span
                  key={`${vehicle.id}-feat-${f}`}
                  className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                  
                      {f}
                    </span>
                )}
                </div>

                {/* Select Button */}
                {vehicle.available ?
              <button
                onClick={() => handleSelectCar(vehicle)}
                className={`w-full py-2.5 rounded-lg text-sm font-bold transition-all duration-200 flex items-center justify-center gap-1 ${
                selectedVehicle?.id === vehicle.id ?
                'bg-primary text-white glow-blue-sm' : 'btn-primary'}`
                }>
                
                    {selectedVehicle?.id === vehicle.id ?
                <>
                        <CheckCircle size={14} /> Selected
                      </> :

                <>
                        Select Car <ChevronRight size={14} />
                      </>
                }
                  </button> :

              <button
                disabled
                className="w-full py-2.5 rounded-lg text-sm font-bold bg-muted/30 text-muted-foreground cursor-not-allowed flex items-center justify-center gap-1">
                
                    <XCircle size={14} /> Currently Booked
                  </button>
              }
              </div>
            </div>
          )}
        </div>

        {/* Booking Form Area */}
        <div id="booking-form-area">
          {showBooking && selectedVehicle &&
          <div className="glass-card border border-primary/20 rounded-2xl p-6 lg:p-8 relative animate-slide-up">
              <button
              onClick={() => {setShowBooking(false);setSelectedVehicle(null);}}
              className="absolute top-4 right-4 text-muted-foreground hover:text-white transition-colors"
              aria-label="Close booking form">
                <X size={20} />
              </button>
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white mb-1">Complete Your Booking</h3>
                <p className="text-muted-foreground text-sm">
                  Fill in your details and we&apos;ll connect you with a driver instantly.
                </p>
              </div>
              <BookingModal vehicle={selectedVehicle} onClose={() => {setShowBooking(false);setSelectedVehicle(null);}} />
            </div>
          }

          {!showBooking &&
          <div className="text-center py-12 glass-card border border-border/20 rounded-2xl">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">🚖</span>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Select a Vehicle Above</h3>
              <p className="text-muted-foreground text-sm">
                Choose your preferred cab from the fleet above to reveal the booking form.
              </p>
            </div>
          }
        </div>
      </div>
    </section>);

}